import { useCallback, useState } from "react"
import { DateTime } from "timezonecomplete"
import {
  TIMESCALE_PERIOD_VALUES,
  normaliseSelection,
  type AppliedRange,
  type EntityBoundarySource,
  type NormaliseResult,
  type StagedSelection,
  type TimescalePeriodValue,
} from "./TimeRange"

/** Relative preset used when no valid preference is persisted. */
const DEFAULT_PERIOD: TimescalePeriodValue = "7d"

const isTimescalePeriodValue = (value: string | undefined): value is TimescalePeriodValue =>
  TIMESCALE_PERIOD_VALUES.includes(value as TimescalePeriodValue)

/** Real clock, used only when the caller does not inject `now`. */
const systemNow = (): DateTime => DateTime.nowUtc()

export interface UseTimeRangeControllerOptions {
  /** Asset assignment or System installation bounds. */
  boundarySource: EntityBoundarySource
  /** Browser IANA timezone, supplied by the caller so the hook reads no browser globals. */
  browserTimeZone: string
  /** Persisted relative preset, read once to initialise. Absent or invalid values fall back to `7d`. */
  persistedPreset?: string
  /** Called with the preset after each successful relative Apply, and never for Custom. */
  onRelativePresetApplied: (preset: TimescalePeriodValue) => void
  /** Injectable clock. Defaults to the real clock. */
  now?: () => DateTime
}

export interface UseTimeRangeControllerResult {
  /** Canonical UTC range for graph requests. Only a successful Apply or Refresh changes it. */
  applied: AppliedRange
  /** Normalise and commit a staged selection. A result with errors leaves `applied` unchanged. */
  apply: (selection: StagedSelection) => NormaliseResult
  /** Recalculate a relative range from its effective end, and signal a refetch for any range. */
  refresh: () => void
  /** Changes on every Refresh, so an unchanged range still refetches. Not a GraphQL variable. */
  refreshKey: number
}

function calculateRelativeRange(
  preset: TimescalePeriodValue,
  source: EntityBoundarySource,
  now: DateTime,
  browserTimeZone: string
): AppliedRange {
  const { applied } = normaliseSelection({ mode: "relative", preset }, source, now, browserTimeZone)
  if (!applied) throw new Error(`Relative range "${preset}" could not be calculated`)
  return applied
}

/**
 * Owns the applied time range for a Timescale monitoring view.
 *
 * Staging lives in `TimeRangeControl`; this hook only sees a selection when it is
 * applied. Preferences are written through `onRelativePresetApplied`, which runs
 * after a successful relative Apply and nowhere else (not on initialisation,
 * Refresh or Custom Apply), so Custom ranges stay in React state for the session.
 */
export function useTimeRangeController({
  boundarySource,
  browserTimeZone,
  persistedPreset,
  onRelativePresetApplied,
  now = systemNow,
}: UseTimeRangeControllerOptions): UseTimeRangeControllerResult {
  const [applied, setApplied] = useState<AppliedRange>(() =>
    calculateRelativeRange(
      isTimescalePeriodValue(persistedPreset) ? persistedPreset : DEFAULT_PERIOD,
      boundarySource,
      now(),
      browserTimeZone
    )
  )
  const [refreshKey, setRefreshKey] = useState(0)

  const apply = useCallback(
    (selection: StagedSelection): NormaliseResult => {
      const result = normaliseSelection(selection, boundarySource, now(), browserTimeZone)

      if (result.applied && result.errors.length === 0) {
        setApplied(result.applied)
        if (selection.mode === "relative") onRelativePresetApplied(selection.preset)
      }

      return result
    },
    [boundarySource, browserTimeZone, now, onRelativePresetApplied]
  )

  const refresh = useCallback(() => {
    // A Custom range keeps its canonical endpoints; only the key changes.
    if (applied.mode === "relative" && applied.preset) {
      setApplied(calculateRelativeRange(applied.preset, boundarySource, now(), browserTimeZone))
    }
    setRefreshKey((key) => key + 1)
  }, [applied, boundarySource, browserTimeZone, now])

  return { applied, apply, refresh, refreshKey }
}
